import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useVeEquipa } from '@/features/auth/AuthContext';
import { useSettings } from '@/features/app/hooks';
import { useObras } from '@/features/obras/hooks';
import {
  useCreateRegisto,
  useDeleteRegisto,
  useDia,
  useEquipaDia,
  useSemanas,
  useSetObservacaoDia,
  useToggleFecho,
  useUpdateRegisto,
} from '@/features/partes/hooks';
import type { Dia, Registo } from '@/types';
import { formatDiaCurto, formatHoras, initials, monthOf, todayISO, weeksBetween } from '@/lib/format';
import type { Language } from '@/i18n';
import { AppLayout } from '@/components/AppLayout';
import { WeekStrip } from '@/components/WeekStrip';
import { RegistoSheet } from '@/components/sheets/RegistoSheet';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorBanner } from '@/components/ui/ErrorBanner';
import { Icon } from '@/components/ui/Icon';
import { Spinner } from '@/components/ui/Spinner';
import { useToast } from '@/components/ui/Toast';

/** Weeks the strip carries at first, how many more each swipe back adds, and the
 *  floor it stops at — half a year of history is well past any payroll question. */
const SEMANAS_INICIAIS = 4;
const SEMANAS_POR_PEDIDO = 4;
const SEMANAS_MAX = 26;

/**
 * The home screen.
 *
 * An eletricista lands straight on their own parte diária. An administrador or
 * encarregado gets a switch: the team's day (who booked what, who has not
 * submitted) or their own hours — because chefia works on site too, and the
 * client's brief puts *every* worker in charge of entering their own hours. From
 * the team view they can also register on somebody's behalf, which is what
 * "alterar registos no ato de inserir a parte diária" needs in practice.
 */
export function HojePage() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage ?? 'pt') as Language;
  const veEquipa = useVeEquipa();
  const [params, setParams] = useSearchParams();
  const data = params.get('data') ?? todayISO();

  const [vista, setVista] = useState<'equipa' | 'propria'>('equipa');
  const mostrarEquipa = veEquipa && vista === 'equipa';

  // The strip always ends on this week, and reaches back far enough to hold the
  // selected day — arriving from Equipa with ?data= in an old month still lands
  // on a strip that shows it.
  const [semanasExtra, setSemanasExtra] = useState(0);
  const semanas = Math.min(
    SEMANAS_MAX,
    Math.max(SEMANAS_INICIAIS + semanasExtra, weeksBetween(data, todayISO()) + 1),
  );
  const semana = useSemanas(todayISO(), semanas);

  const settings = useSettings();
  const diaProprio = useDia(data, undefined, !mostrarEquipa);
  const diaEquipa = useEquipaDia(data, mostrarEquipa);

  const setData = (next: string) => setParams({ data: next }, { replace: true });
  const mostrarCentroCusto = settings.data?.mostrarCentroCusto ?? true;

  const subtitle = mostrarEquipa
    ? `${formatDiaCurto(data, lang)} · ${t('header.todosEletricistas')}`
    : `${formatDiaCurto(data, lang)}${diaProprio.data ? ` · ${diaProprio.data.userNome}` : ''}`;

  return (
    <AppLayout
      title={mostrarEquipa ? t('header.diaEquipa') : t('header.parteDiaria')}
      subtitle={subtitle}
    >
      <div className="flex flex-col gap-4">
        {veEquipa && (
          <div className="flex gap-1 rounded-xl border border-conduril-border bg-white p-1 md:max-w-sm">
            {(['equipa', 'propria'] as const).map((opcao) => (
              <button
                key={opcao}
                type="button"
                onClick={() => setVista(opcao)}
                aria-pressed={vista === opcao}
                className={`flex-1 rounded-lg px-3 py-2 text-[12px] font-semibold transition ${
                  vista === opcao ? 'bg-conduril text-white' : 'text-conduril-muted hover:text-conduril'
                }`}
              >
                {t(opcao === 'equipa' ? 'hoje.vistaEquipa' : 'hoje.vistaPropria')}
              </button>
            ))}
          </div>
        )}

        {semana.dias.length > 0 && (
          <div className="md:max-w-2xl">
            <WeekStrip
              dias={semana.dias}
              selecionado={data}
              onSelect={setData}
              aCarregar={semana.isFetching}
              onMaisSemanas={
                semanas < SEMANAS_MAX
                  ? () => setSemanasExtra((n) => n + SEMANAS_POR_PEDIDO)
                  : undefined
              }
            />
          </div>
        )}

        {mostrarEquipa ? (
          <EquipaDiaView data={data} query={diaEquipa} mostrarCentroCusto={mostrarCentroCusto} />
        ) : (
          <ParteProprioView
            data={data}
            query={diaProprio}
            mostrarCentroCusto={mostrarCentroCusto}
          />
        )}
      </div>
    </AppLayout>
  );
}

// ---- The worker's own day ----

function ParteProprioView({
  data,
  query,
  mostrarCentroCusto,
}: {
  data: string;
  query: ReturnType<typeof useDia>;
  mostrarCentroCusto: boolean;
}) {
  const { t, i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage ?? 'pt') as Language;
  const toast = useToast();
  const obras = useObras(monthOf(data));
  const criar = useCreateRegisto();
  const alterar = useUpdateRegisto();
  const apagar = useDeleteRegisto();
  const fechar = useToggleFecho();
  const guardarObs = useSetObservacaoDia();

  const [sheetOpen, setSheetOpen] = useState(false);
  const [editar, setEditar] = useState<Registo | null>(null);
  const [obsDia, setObsDia] = useState<string | null>(null);

  const dia = query.data;
  const ativas = useMemo(() => (obras.data ?? []).filter((o) => !o.arquivada), [obras.data]);

  if (query.isLoading) return <Loading />;
  if (query.error) return <ErrorBanner error={query.error} onRetry={() => void query.refetch()} />;
  if (!dia) return null;

  const alvo = dia.horasAlvo;
  const estado =
    dia.totalHoras === 0
      ? t('hoje.semRegisto')
      : dia.totalHoras < alvo
        ? t('hoje.faltam', { horas: formatHoras(alvo - dia.totalHoras) })
        : dia.totalHoras > alvo
          ? t('hoje.extra', { horas: formatHoras(dia.totalHoras - alvo) })
          : t('hoje.completo');

  const abrirNovo = () => {
    setEditar(null);
    setSheetOpen(true);
  };

  const abrirEdicao = (registo: Registo) => {
    if (!dia.editavel) {
      toast.show(t('hoje.bloqueado'));
      return;
    }
    setEditar(registo);
    setSheetOpen(true);
  };

  const guardar = async (input: { obraId: string; horas: number; observacao: string | null }) => {
    const obra = ativas.find((o) => o.id === input.obraId);
    try {
      if (editar) {
        await alterar.mutateAsync({ id: editar.id, ...input });
        toast.show(t('sheet.alterada', { obra: obra?.nome ?? '' }));
      } else {
        await criar.mutateAsync({ data, ...input });
        toast.show(t('sheet.criada', { horas: formatHoras(input.horas), obra: obra?.nome ?? '' }));
      }
      setSheetOpen(false);
    } catch (err) {
      toast.show(err instanceof Error ? err.message : t('common.error'));
    }
  };

  const remover = async () => {
    if (!editar) return;
    try {
      await apagar.mutateAsync(editar.id);
      toast.show(t('sheet.apagada'));
      setSheetOpen(false);
    } catch (err) {
      toast.show(err instanceof Error ? err.message : t('common.error'));
    }
  };

  const alternarFecho = async () => {
    try {
      await fechar.mutateAsync({ data });
      toast.show(
        dia.fechada
          ? t('hoje.reabertaToast')
          : t('hoje.fechadaToast', { data: formatDiaCurto(data, lang) }),
      );
    } catch (err) {
      toast.show(err instanceof Error ? err.message : t('hoje.semRegistosParaFechar'));
    }
  };

  const ocupado = criar.isPending || alterar.isPending || apagar.isPending;

  return (
    <>
      {!dia.editavel && dia.motivoBloqueio && (
        <div className="flex items-start gap-2.5 rounded-xl border border-aviso-border bg-aviso-bg px-3.5 py-3">
          <Icon name="lock" className="mt-px shrink-0 text-[17px] text-[#b0761a]" />
          <span className="text-[12px] leading-relaxed text-aviso-text">{dia.motivoBloqueio}</span>
        </div>
      )}

      <section className="card px-4 pb-3.5 pt-4">
        <div className="flex items-end justify-between gap-3">
          <div>
            <div className="label-eyebrow">{t('hoje.totalDia')}</div>
            <div className="num mt-2 text-[33px] font-semibold leading-none tracking-tight text-conduril">
              {formatHoras(dia.totalHoras)}
            </div>
          </div>
          <div className="text-right">
            <div className="text-[11px] leading-snug text-[#7c8393]">
              {t('hoje.previstas', { horas: alvo })}
            </div>
            <div className="mt-1 text-[11px] font-semibold tracking-wide text-conduril">{estado}</div>
          </div>
        </div>
        <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-[#eceef4]">
          <div
            className="h-full rounded-full transition-[width] duration-300"
            style={{
              width: `${Math.min(100, (dia.totalHoras / alvo) * 100)}%`,
              background: dia.totalHoras > alvo ? '#b07d1c' : '#14257d',
            }}
          />
        </div>
      </section>

      <div className="mt-1 flex items-center justify-between">
        <div className="label-eyebrow">{t('hoje.obrasDoDia')}</div>
        <div className="num text-[11px] text-conduril-muted">
          {t('hoje.registos', { count: dia.registos.length })}
        </div>
      </div>

      {dia.registos.length === 0 ? (
        <EmptyState message={t('hoje.vazio')} />
      ) : (
        <ul className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
          {dia.registos.map((registo) => (
            <li key={registo.id}>
              <button
                type="button"
                onClick={() => abrirEdicao(registo)}
                className="w-full rounded-2xl border border-conduril-border bg-white p-3.5 text-left shadow-card transition active:scale-[0.995]"
              >
                <span className="flex items-center justify-between gap-3">
                  <span className="flex min-w-0 flex-col gap-1">
                    <span className="truncate text-[15px] font-semibold leading-tight text-conduril-ink">
                      {registo.obraNome}
                    </span>
                    {mostrarCentroCusto && registo.obraCentroCusto && (
                      <span className="num text-[10px] tracking-wide text-conduril-muted">
                        {registo.obraCentroCusto}
                      </span>
                    )}
                  </span>
                  <span className="num shrink-0 text-[19px] font-semibold text-conduril">
                    {formatHoras(registo.horas)}
                  </span>
                </span>
                {registo.observacao && (
                  <span className="mt-2.5 flex gap-2 border-t border-[#eef0f5] pt-2.5">
                    <span className="num shrink-0 text-[9px] tracking-widest text-[#a8adba]">
                      {t('hoje.obs')}
                    </span>
                    <span className="text-[12px] leading-snug text-[#5c6373]">{registo.observacao}</span>
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* The brief asks for an observação attached to the day itself, not only
          to each obra — "Ter um campo de observação associado a cada dia". */}
      <section className="card p-4">
        <div className="label-eyebrow">{t('hoje.obsDiaTitulo')}</div>
        <textarea
          rows={2}
          disabled={!dia.editavel}
          className="field mt-2 resize-none bg-[#fbfbfd] text-[13px] leading-relaxed disabled:bg-[#f4f5f8] disabled:text-conduril-muted"
          placeholder={t('hoje.obsDiaPlaceholder')}
          maxLength={500}
          value={obsDia ?? dia.observacao ?? ''}
          onChange={(e) => setObsDia(e.target.value)}
        />
        {dia.editavel && obsDia !== null && obsDia !== (dia.observacao ?? '') && (
          <button
            type="button"
            disabled={guardarObs.isPending}
            onClick={() => {
              void (async () => {
                try {
                  await guardarObs.mutateAsync({ data, observacao: obsDia.trim() || null });
                  setObsDia(null);
                  toast.show(t('hoje.obsDiaGuardada'));
                } catch (err) {
                  toast.show(err instanceof Error ? err.message : t('common.error'));
                }
              })();
            }}
            className="btn-ghost mt-2.5 w-full sm:w-auto"
          >
            {t('common.save')}
          </button>
        )}
      </section>

      {dia.editavel && (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <button type="button" onClick={abrirNovo} className="btn-primary sm:w-auto sm:px-6">
            <Icon name="add" className="text-[19px]" /> {t('hoje.adicionar')}
          </button>
          <button
            type="button"
            onClick={() => void alternarFecho()}
            disabled={fechar.isPending}
            className={`w-full rounded-2xl border px-4 py-3.5 text-[13px] font-semibold transition sm:w-auto sm:px-6 ${
              dia.fechada
                ? 'border-ok-border bg-ok-bg text-ok-text'
                : 'border-[#dfe2ec] bg-white text-[#5c6373]'
            }`}
          >
            {dia.fechada ? t('hoje.fechada') : t('hoje.fechar')}
          </button>
        </div>
      )}

      <RegistoSheet
        open={sheetOpen}
        obras={ativas}
        mostrarCentroCusto={mostrarCentroCusto}
        registo={editar}
        busy={ocupado}
        subtitle={`${formatDiaCurto(data, lang).toUpperCase()} · ${dia.userNome.split(' ')[0].toUpperCase()}`}
        onClose={() => setSheetOpen(false)}
        onSave={(input) => void guardar(input)}
        onDelete={editar ? () => void remover() : undefined}
      />
    </>
  );
}

// ---- The team's day, for administradores and encarregados ----

function EquipaDiaView({
  data,
  query,
  mostrarCentroCusto,
}: {
  data: string;
  query: ReturnType<typeof useEquipaDia>;
  mostrarCentroCusto: boolean;
}) {
  const { t, i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage ?? 'pt') as Language;
  const toast = useToast();
  const obras = useObras(monthOf(data));
  const criar = useCreateRegisto();
  const alterar = useUpdateRegisto();
  const apagar = useDeleteRegisto();

  // Chefia books on somebody's behalf, so the sheet has to remember *whose* day
  // it is editing, not just which line.
  const [alvo, setAlvo] = useState<{ userId: string; nome: string; registo: Registo | null } | null>(null);

  const ativas = useMemo(() => (obras.data ?? []).filter((o) => !o.arquivada), [obras.data]);

  if (query.isLoading) return <Loading />;
  if (query.error) return <ErrorBanner error={query.error} onRetry={() => void query.refetch()} />;
  const equipa = query.data;
  if (!equipa) return null;

  const guardar = async (input: { obraId: string; horas: number; observacao: string | null }) => {
    if (!alvo) return;
    const obra = ativas.find((o) => o.id === input.obraId);
    try {
      if (alvo.registo) {
        await alterar.mutateAsync({ id: alvo.registo.id, ...input });
        toast.show(t('sheet.alterada', { obra: obra?.nome ?? '' }));
      } else {
        await criar.mutateAsync({ data, userId: alvo.userId, ...input });
        toast.show(t('sheet.criadaPara', {
          horas: formatHoras(input.horas),
          obra: obra?.nome ?? '',
          nome: alvo.nome.split(' ')[0],
        }));
      }
      setAlvo(null);
    } catch (err) {
      toast.show(err instanceof Error ? err.message : t('common.error'));
    }
  };

  const remover = async () => {
    if (!alvo?.registo) return;
    try {
      await apagar.mutateAsync(alvo.registo.id);
      toast.show(t('sheet.apagada'));
      setAlvo(null);
    } catch (err) {
      toast.show(err instanceof Error ? err.message : t('common.error'));
    }
  };

  return (
    <>
      <section className="rounded-2xl bg-conduril p-4 md:p-5">
        <div className="flex items-end justify-between gap-3">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#94a1dd]">
              {t('equipaDia.horasEquipa')}
            </div>
            <div className="num mt-2.5 text-[30px] font-semibold leading-none text-white">
              {formatHoras(equipa.totalHoras)}
            </div>
          </div>
          <div className="text-right text-[11px] font-medium leading-relaxed text-[#b3bce8]">
            {equipa.semParteDiaria > 0
              ? t('equipaDia.semParte', { count: equipa.semParteDiaria })
              : t('equipaDia.todosSubmeteram')}
          </div>
        </div>
      </section>

      <div className="label-eyebrow mt-1">{t('equipaDia.titulo')}</div>

      {equipa.partes.length === 0 ? (
        <EmptyState message={t('equipaDia.semEletricistas')} />
      ) : (
        <ul className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
          {equipa.partes.map((parte) => (
            <PessoaCard
              key={parte.userId}
              parte={parte}
              editavel={equipa.editavel}
              mostrarCentroCusto={mostrarCentroCusto}
              onAdicionar={() => setAlvo({ userId: parte.userId, nome: parte.userNome, registo: null })}
              onEditar={(registo) =>
                setAlvo({ userId: parte.userId, nome: parte.userNome, registo })
              }
            />
          ))}
        </ul>
      )}

      <RegistoSheet
        open={alvo !== null}
        obras={ativas}
        mostrarCentroCusto={mostrarCentroCusto}
        registo={alvo?.registo}
        busy={criar.isPending || alterar.isPending || apagar.isPending}
        subtitle={`${formatDiaCurto(data, lang).toUpperCase()} · ${(alvo?.nome ?? '').split(' ')[0].toUpperCase()}`}
        onClose={() => setAlvo(null)}
        onSave={(input) => void guardar(input)}
        onDelete={alvo?.registo ? () => void remover() : undefined}
      />
    </>
  );
}

function PessoaCard({
  parte,
  editavel,
  mostrarCentroCusto,
  onAdicionar,
  onEditar,
}: {
  parte: Dia;
  editavel: boolean;
  mostrarCentroCusto: boolean;
  onAdicionar: () => void;
  onEditar: (registo: Registo) => void;
}) {
  const { t } = useTranslation();
  const estado = parte.fechada
    ? t('equipaDia.estadoFechada')
    : parte.registos.length > 0
      ? t('equipaDia.estadoPreenchimento')
      : t('equipaDia.estadoSemRegisto');

  return (
    <li className="flex flex-col rounded-2xl border border-pessoa-border bg-pessoa-bg p-3.5 shadow-card">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-white text-[11px] font-semibold text-conduril">
            {initials(parte.userNome)}
          </span>
          <div className="min-w-0">
            <div className="truncate text-[14px] font-semibold leading-tight text-conduril-ink">
              {parte.userNome}
            </div>
            <div className="mt-1 text-[10px] text-conduril-muted">{estado}</div>
          </div>
        </div>
        <span className="num shrink-0 text-[17px] font-semibold text-conduril">
          {formatHoras(parte.totalHoras)}
        </span>
      </div>

      {parte.registos.length > 0 ? (
        <div className="mt-3 flex flex-col border-t border-pessoa-line pt-1">
          {parte.registos.map((registo) => (
            <button
              key={registo.id}
              type="button"
              disabled={!editavel}
              onClick={() => onEditar(registo)}
              className="-mx-1.5 flex items-start justify-between gap-3 rounded-lg px-1.5 py-2 text-left transition
                         enabled:hover:bg-pessoa-hover disabled:cursor-default"
            >
              <span className="min-w-0">
                <span className="block truncate text-[13px] font-medium leading-snug text-conduril-ink">
                  {registo.obraNome}
                  {mostrarCentroCusto && registo.obraCentroCusto && (
                    <span className="num ml-2 text-[10px] text-conduril-muted">
                      {registo.obraCentroCusto}
                    </span>
                  )}
                </span>
                <span className="mt-1 block text-[11px] leading-snug text-[#8a8f9c]">
                  {registo.observacao ?? '—'}
                </span>
              </span>
              <span className="num shrink-0 text-[13px] font-semibold text-[#5c6373]">
                {formatHoras(registo.horas)}
              </span>
            </button>
          ))}
        </div>
      ) : (
        <div className="pt-2 text-[12px] leading-snug text-[#a8710f]">
          {t('equipaDia.aindaNaoSubmeteu')}
        </div>
      )}

      {parte.observacao && (
        <div className="mt-2 flex gap-2 border-t border-pessoa-line pt-2">
          <span className="num shrink-0 text-[9px] tracking-widest text-[#a8adba]">
            {t('hoje.obsDia')}
          </span>
          <span className="text-[11px] leading-snug text-[#5c6373]">{parte.observacao}</span>
        </div>
      )}

      {editavel && (
        <button
          type="button"
          onClick={onAdicionar}
          className="mt-3 flex items-center justify-center gap-1.5 rounded-xl border border-dashed border-[#aec5a4]
                     py-2 text-[12px] font-semibold text-conduril transition hover:border-conduril hover:bg-white"
        >
          <Icon name="add" className="text-[16px]" /> {t('equipaDia.adicionarPara', { nome: parte.userNome.split(' ')[0] })}
        </button>
      )}
    </li>
  );
}

function Loading() {
  return (
    <div className="flex justify-center py-10">
      <Spinner />
    </div>
  );
}
